import React, { useState, useRef, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import { Table, Image, InputNumber, Tag } from "antd";
import { showConfirmDlgWidget } from 'components/ConfirmDlgWidget/showConfirmDlgWidget';
import dragula from "dragula";
import "dragula/dist/dragula.css";
import { apiUpdateRecipePosition } from 'services/recipeListService';
import { DragOutlined } from '@ant-design/icons';
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const getIndexInParent = (el) => Array.from(el.parentNode.children).indexOf(el);

// Component for single image with hover effect
const SingleImageWithHover = ({ src }) => {
    return (
        <div
            className="thumbnail-single-wrapper"
            style={{
                position: "relative",
                display: "inline-block",
                width: "80px",
                height: "80px",
                cursor: "pointer"
            }}
        >
            <Image
                className="room-thumbnail-img thumbnail-single-image"
                src={src}
                style={{
                    width: "80px",
                    height: "80px",
                    objectFit: "cover",
                    cursor: "pointer",
                    borderRadius: "6px",
                    transition: "all 0.3s ease"
                }}
            />
        </div>
    );
};

function TableWidget(props) {
    const [isBusy, setIsBusy] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const { dataList, setDataList, onUpdateAction, onDeleteAction, onDetailAction } = props;

    useEffect(() => {
        let start;
        let end;
        const container = document.querySelector(".ant-table-tbody");
        const drake = dragula([container], {
            moves: (el) => {
                start = getIndexInParent(el);
                return true;
            },
        });

        drake.on("drop", (el) => {
            end = getIndexInParent(el);
            handleReorder(start, end);
        });
    }, []);

    useEffect(() => {
        if (dataList !== undefined) {
            setDataSource([...dataList.map((info) => {
                return { ...info, key: info.id, icon: <DragOutlined type="swap" /> };
            })]);
        }
    }, [dataList]);
    if (dataList === undefined) return <div />

    const columns = [
        {
            title: <DragOutlined type="swap" />,
            dataIndex: "icon",
        },
        {
            title: "ID",
            dataIndex: "id",
        },
        {
            title: "Thumbnail",
            dataIndex: "thumbnail_image",
            render: (text, record) => {
                // Handle multiple images - could be array, single string, or comma-separated string
                let images = record.thumbnail_image;

                if (!images || images === null) {
                    return (
                        <img
                            className="room-thumbnail-img"
                            src={'/assets/icons/ic_picture.svg'}
                            style={{
                                width: "80px",
                                height: "80px",
                                objectFit: "cover",
                                cursor: "pointer",
                                borderRadius: "6px"
                            }}
                        />
                    )
                }

                // Convert to array if needed
                if (typeof images === 'string') {
                    // Try to parse as JSON first
                    try {
                        const parsed = JSON.parse(images);
                        if (Array.isArray(parsed)) {
                            images = parsed;
                        } else {
                            // Check if it's comma-separated
                            images = images.includes(',') ? images.split(',').map(img => img.trim()) : [images];
                        }
                    } catch (e) {
                        // Not JSON, check if comma-separated
                        images = images.includes(',') ? images.split(',').map(img => img.trim()) : [images];
                    }
                }

                // If it's an array, display all images
                if (Array.isArray(images) && images.length > 0) {
                    return (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", maxWidth: "300px" }}>
                            {images.map((imgUrl, index) => (
                                <Image
                                    key={index}
                                    className="room-thumbnail-img"
                                    src={imgUrl}
                                    style={{
                                        marginBottom: "4px",
                                        width: "80px",
                                        height: "80px",
                                        objectFit: "cover",
                                        cursor: "pointer",
                                        borderRadius: "6px"
                                    }}
                                    preview={{
                                        mask: images.length > 1 ? `${index + 1}/${images.length}` : undefined
                                    }}
                                />
                            ))}
                        </div>
                    );
                }

                // Fallback: single image
                return (
                    <SingleImageWithHover src={images} />
                );
            },
        },
        {
            title: "Name",
            dataIndex: "name",
            align: "center",
            sorter: {
              compare: (a, b) => {
                // Strip HTML tags for comparison
                const stripHtml = (html) => {
                  const tmp = document.createElement("DIV");
                  tmp.innerHTML = html || "";
                  return tmp.textContent || tmp.innerText || "";
                };
                return stripHtml(a.name).localeCompare(stripHtml(b.name));
              },
            },
            render: (text, record) => {
              return (
                <div
                  style={{
                    maxWidth: 300,
                    textAlign: "left",
                  }}
                  className="table-quill-display"
                >
                  <ReactQuill
                    value={record.name || ""}
                    readOnly={true}
                    theme="snow"
                    modules={{ toolbar: false }}
                  />
                </div>
              );
            },
          },
        {
            title: "Publish",
            dataIndex: "status",
            align: "center",
            sorter: {
                compare: (a, b) => a.status.localeCompare(b.status),
            },
            render: (text, record) => {
                return (
                    <Tag
                      color={
                        record.status === "1" ? "var(--blueColor)" : "var(--orangeColor)"
                      }
                    >
                      {record.status === "1" ? "Yes" : "No"}
                    </Tag>
                  );
            }
        },
        {
            title: "Updated At",
            dataIndex: "update_timestamp",
            align: "center",
            sorter: {
                compare: (a, b) => a.update_timestamp.localeCompare(b.update_timestamp),
            },
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            render: (text, record) => {
                return (
                    <div size="middle" direction={"vertical"}>
                        <a style={{ color: "var(--orangeColor)", marginLeft: 20 }} className="edit-btn"
                            onClick={() => {
                                onUpdateAction(record)
                            }}
                        >
                            Edit
                        </a>
                        <a style={{ color: "var(--redColor)", marginLeft: 20 }} className="del-btn" onClick={async () => {
                            var res = await showConfirmDlgWidget({ title: "Delete Confirmation", content: "Are you sure to delete this record?" });
                            if (res == false) return;
                            onDeleteAction(record)
                        }}>
                            Delete
                        </a>
                    </div>
                );
            },
        },
    ];

    const handleReorder = (dragIndex, draggedIndex) => {
        setDataSource((oldState) => {
            const newState = [...oldState];
            const item = newState.splice(dragIndex, 1)[0];
            newState.splice(draggedIndex, 0, item);
            apiUpdateRecipePosition(newState)
            return newState;
        });
    };



    return (
        <>
            <style>
                {`
                    .table-quill-display .ql-container {
                        border: none !important;
                        font-size: 14px;
                    }
                    .table-quill-display .ql-editor {
                        padding: 0 !important;
                    }
                    .table-quill-display .ql-editor.ql-blank::before {
                        display: none;
                    }
                    .room-thumbnail-img {
                        cursor: pointer !important;
                    }
                    .room-thumbnail-img img {
                        cursor: pointer !important;
                        border-radius: 6px !important;
                    }
                    .ant-image-img {
                        border-radius: 6px !important;
                    }
                    .thumbnail-single-wrapper {
                        position: relative;
                        display: inline-block;
                        width: 80px;
                        height: 80px;
                    }
                    .thumbnail-single-wrapper .ant-image {
                        width: 80px !important;
                        height: 80px !important;
                        position: relative;
                        transition: all 0.3s ease;
                    }
                    .thumbnail-single-wrapper .ant-image-img {
                        width: 80px !important;
                        height: 80px !important;
                        transition: all 0.3s ease;
                    }
                    .thumbnail-single-wrapper:hover .ant-image {
                        transform: scale(1.1);
                        filter: brightness(0.7);
                    }
                    .thumbnail-single-wrapper:hover .ant-image-img {
                        transform: scale(1.1);
                        filter: brightness(0.7);
                    }
                `}
            </style>
            <div className="list-table">
                <Table
                    pagination={false}
                    columns={columns}
                    dataSource={dataSource}
                />
            </div>
        </>
    )
}

export default withRouter(TableWidget)
